/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import { IQuickviewViewProps } from '@msdyn365-commerce-modules/buybox';
import { IModuleProps, Module, Node, INodeProps } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect, useState } from 'react';
import { IQuickviewProps as IQuickviewPropsExtend } from '../definition-extensions/quickview.ext.props.autogenerated';
import { AiFillFileWord, AiFillFilePpt, AiFillFilePdf, AiFillFileExcel, AiOutlineHtml5, AiFillFile, AiFillFileImage } from 'react-icons/ai';
import { getArtifactsByItemAsync } from '../../../actions/extensions/DataActionExtension.g';
import { AddToCartComponent } from './components/addtocart.component';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import {
    FaLeanpub,
    FaGoogleDrive,
    FaFileWord,
    FaFileImage,
    FaFileCsv,
    FaFileCode,
    FaFileVideo,
    FaFileInvoice,
    FaFileSignature,
    FaFileArchive,
    FaFileAudio
} from 'react-icons/fa';
import {
    BsFillFileEarmarkBarGraphFill,
    BsFileEarmarkRichtextFill,
    BsFileEarmarkPostFill,
    BsFileEarmarkFontFill,
    BsFileEarmarkCodeFill,
    BsFileEarmarkEaselFill
} from 'react-icons/bs';
import { RiFileGifFill } from 'react-icons/ri';
import { SiMicrosoftoutlook } from 'react-icons/si';
// import { getCartState } from '@msdyn365-commerce/global-state';

import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { ACTION_BUTTONS, ADD_TO_KETE, ADD_TO_KETE_SITE_LANGUAGE, KWR } from '../../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../../utilities/language-change';
import { HiDocumentReport } from 'react-icons/hi';
export interface IProductCollectionAddToCartViewProps {
    ContainerProps: INodeProps;
    button?: React.ReactNode;
}

/**
 * RenderQuickViewPopup.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        cartContainerProps,
        context,
        resources,
        data,
        productDetails
    } = props;
    const moduleType = props.config.moduleType;

    const [Artifacts, setArtifacts] = useState([]);
    const [selectedArtifacts, setSelectedArtifacts] = useState<string[]>([]);
    const [previouslySelectedArtifacts, setpreviouslySelectedArtifacts] = useState<string[]>([]);
    const [product, setProduct] = useState<any>();
    const [resourcePageURL, setResourcePageURL] = useState<string>('');
    const [previouslySelectedResourcePageURL, setpreviouslySelectedResourcePageURL] = useState<string>('');
    const [productUrl, setproductUrlURL] = useState<string>('');
    const [isEdit, setIsEdit] = React.useState(false);
    const [cartLineData, setCartLineData] = React.useState<CartLine>();

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredLanguage: any = getUserPreferredLanguage(KWR, ACTION_BUTTONS, userPrefLang, toggleLang, context);
    const preferredKeteSiteLanguage: any = getUserPreferredLanguage(KWR, ADD_TO_KETE_SITE_LANGUAGE, userPrefLang, toggleLang, context);

    // Language Variables
    const languageResources = {
        selectAllText: resources.selectAllText,
        selectAllTextInMaori: resources.selectAllTextInMaori,
        selectResourcesText: resources.selectResourcesText,
        selectResourcesTextInMaori: resources.selectResourcesTextInMaori,
        downloadText: resources.downloadText,
        downloadTextInMaori: resources.downloadTextInMaori,
        addToKeteButtonText: preferredLanguage === 'maori' ? resources.addToKeteTextInMaori : resources.addToKeteText,
        updateKeteButtonText: preferredLanguage === 'maori' ? resources.updateketeTextInMaori : resources.updateketeText
    };

    const renderBodyContent = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
        const {
            loading,
            // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
            ProductInfoContainerProps,
            MediaGalleryContainerProps,
            CarouselProps
            // data
        } = props;

        useEffect(() => {
            getArtifacts()?.then((artifacts: any) => {
                setArtifacts(artifacts);
            });
            if (props.productDetails?.product) {
                setProduct(props.productDetails.product);
                let productUrl = props.productDetails.product
                    ? getProductPageUrlSync(
                          props.productDetails?.product.Name ?? '',
                          props.productDetails?.product.RecordId,
                          props.context.actionContext,
                          undefined
                      )
                    : '';
                productUrl = window.location.origin + productUrl;
                setproductUrlURL(productUrl);
                handleEditFunctionality();
            }
        }, [props.productDetails?.product]);

        function handleEditFunctionality() {
            const productID = props.productDetails?.product?.RecordId;
            const cartLine = props.data.cart.result?.cart.CartLines?.find(cartline => cartline.ProductId === productID);
            setCartLineData(cartLine);
            let artifactString: any =
                cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 0
                    ? cartLine.ExtensionProperties[0]?.Value?.StringValue
                    : '';
            let htmlResourceURL: any =
                cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 1
                    ? cartLine.ExtensionProperties[1]?.Value?.StringValue
                    : '';
            let productURL: any =
                cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 2
                    ? cartLine.ExtensionProperties[2]?.Value?.StringValue
                    : '';
            let artifacts = artifactString ? JSON.parse(artifactString) : null;
            if (artifacts) {
                let selectedArtifactIds = artifacts.map(function(el: any) {
                    return el.ArtifactId;
                });
                setpreviouslySelectedArtifacts(selectedArtifactIds);
                setpreviouslySelectedResourcePageURL(htmlResourceURL);
                if ((selectedArtifactIds && selectedArtifactIds.length > 0) || htmlResourceURL) {
                    setSelectedArtifacts(selectedArtifactIds);
                    setArtifacts(artifacts);
                    setResourcePageURL(htmlResourceURL);
                    setproductUrlURL(productURL);
                    setIsEdit(true);
                }
            }
        }

        function getArtifacts() {
            try {
                const context: any = { callerContext: props.context.actionContext };
                console.log('Get Props inside get Artifacts');
                // return getArtifactsByItemAsync(context, 'NZH30').then(result => result);
                return getArtifactsByItemAsync(context, props.productDetails?.product?.ItemId as string).then((result: any) => {
                    return result.filter((item: any) => {
                        return (
                            item?.FileType?.toUpperCase() !== 'IMSCC' &&
                            item?.FileType?.toUpperCase() !== 'WEBPAGE' &&
                            !(item?.MediaName as string).includes('CogSearchIndexHelper') &&
                            !item?.IsRetire
                        );
                    });
                });
            } catch (err) {
                console.log(err);
                return;
            }
        }

        if (loading) {
            const { context, resources } = props;

            // Identifying Language
            const toggleLang = getToggleLang(context);
            const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
            const preferredLanguage: any = getUserPreferredLanguage(KWR, ADD_TO_KETE, userPrefLang, toggleLang, context);

            const loadingText = preferredLanguage === 'maori' ? resources.loadingMessageInMaori : resources.loadingMessage;
            return <Spinner className='ms-quickView__loading__icon' label={loadingText} size={SpinnerSize.large} />;
            // return <>{loading}</>;
        }

        function fileCheckboxOnChange(event: React.ChangeEvent<HTMLInputElement>, artifactId: string) {
            if (artifactId != '0') {
                let selectedArtifactIds = selectedArtifacts;

                if (event?.target?.checked) {
                    selectedArtifactIds = [...selectedArtifactIds, artifactId];
                } else {
                    selectedArtifactIds = selectedArtifacts.filter(el => el !== artifactId);
                }
                setSelectedArtifacts(selectedArtifactIds);
            } else {
                if (event?.target?.checked) {
                    setResourcePageURL(productUrl);
                } else {
                    setResourcePageURL('');
                }
            }
        }


        function fnAreAllSelected():boolean {
            let returnValue = false;
            if ((selectedArtifacts.length === Artifacts.length) && (resourcePageURL !== '')) {returnValue = true;}
            return returnValue;
        }

        function fnOnSelectallClick() {
            let selectedArtifactIds = Artifacts.map(function(item: any) {
                return item.ArtifactId;
            });
            setSelectedArtifacts(selectedArtifactIds);
            //setSelectedArtifacts(Artifacts.map((artifact: any) => artifact.id)); 
            setResourcePageURL(productUrl);
        }

        function fnOnDeselectallClick() {
            selectedArtifacts.splice(0, selectedArtifacts.length);
            setResourcePageURL('');
        }

        function renderWordFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFileWord size={50}></AiFillFileWord> <span>{fileName}</span>
                    <input checked={checked} onChange={e => fileCheckboxOnChange(e, artifactId)} type='checkbox' aria-label={fileName} />
                </p>
            );
        }

        function renderExcelFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFileExcel size={50}></AiFillFileExcel> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderPPTFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFilePpt size={50}></AiFillFilePpt> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderPDFFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFilePdf size={50}></AiFillFilePdf> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderEPUBFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaLeanpub size={50}></FaLeanpub> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderImageFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFileImage size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderGoogleDocFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaGoogleDrive size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderHTMLFile(resourceName: string) {
            let checked = resourcePageURL ? true : false;
            return (
                <p>
                    <AiOutlineHtml5 size={50} /> <span>{`${resourceName}`}</span>
                    <input type='checkbox' aria-label='HTML File' checked={checked} onChange={e => fileCheckboxOnChange(e, '0')} />
                </p>
            );
        }

        function renderHTMLFileArtifact(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiOutlineHtml5 size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderAviFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileVideo size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderBmpFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileImage size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderCsvFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileCsv size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderDocFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileWord size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderGifFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <RiFileGifFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderJSONFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileInvoice size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderMhpFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFillFileEarmarkBarGraphFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderCodeFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileCode size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderMsgFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <SiMicrosoftoutlook size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderRTFFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkRichtextFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderSGMLFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkPostFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderTxtFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkFontFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderVsdFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileSignature size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderXmlFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkCodeFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderZipFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileArchive size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderAudioFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileAudio size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderVideoFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileVideo size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderPbixFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <HiDocumentReport size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderUsdzFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkEaselFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderDaeFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <BsFileEarmarkEaselFill size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderXsnFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <FaFileCode size={50} /> <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderotherFiles(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
            return (
                <p>
                    <AiFillFile size={50} />
                    <span>{fileName}</span>
                    <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
                </p>
            );
        }

        function renderDocument(item: any) {
            let checked = selectedArtifacts.some(x => x === item.ArtifactId);

            if (item?.FileType?.toUpperCase() === 'DOC') {
                return renderWordFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'IMAGE') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'EXCEL') {
                return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PPT') {
                return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'EPUB') {
                return renderEPUBFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PDF') {
                return renderPDFFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'GOOGLEDOC') {
                return renderGoogleDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'HTML') {
                return renderHTMLFileArtifact(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'AVI') {
                return renderAviFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'BMP') {
                return renderBmpFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'CSV') {
                return renderCsvFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'DOC') {
                return renderDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'DOCX') {
                return renderDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'GIF') {
                return renderGifFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'HTM') {
                return renderHTMLFileArtifact(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'JPEG') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'JPG') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'JSON') {
                return renderJSONFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MHP') {
                return renderMhpFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MHTML') {
                return renderCodeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MSG') {
                return renderMsgFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PNG') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PPT') {
                return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PPTX') {
                return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'RTF') {
                return renderRTFFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'SGML') {
                return renderSGMLFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'TIF') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'TXT') {
                return renderTxtFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'VSD') {
                return renderVsdFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'WMF') {
                return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'XHTML') {
                return renderCodeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'XLS') {
                return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'XLSX') {
                return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'XML') {
                return renderXmlFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'ZIP') {
                return renderZipFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'WAV') {
                return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MP3') {
                return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'WMA') {
                return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MP4') {
                return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MPEG') {
                return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'MOV') {
                return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'PIBX') {
                return renderPbixFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'USDZ') {
                return renderUsdzFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'DAE') {
                return renderDaeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else if (item?.FileType?.toUpperCase() === 'XSN') {
                return renderXsnFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            } else {
                return renderotherFiles(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
            }
            // } else {
            //     return null;
            // }
        }

        return (
            <>
                <Node {...MediaGalleryContainerProps}>
                    <Node {...CarouselProps} />
                </Node>
                <Node {...ProductInfoContainerProps}>
                    <p className='m-b-4 fw-bold'>
                        {preferredKeteSiteLanguage === 'maori'
                            ? languageResources.selectResourcesTextInMaori
                            : languageResources.selectResourcesText}
                    </p>
                    {Artifacts && (
                        <>
                            <ul className='moe-doclist'>
                                <div className='text-left m-b-4 fw-bold'>
                                    <p>{
                                        preferredKeteSiteLanguage === 'maori'
                                            ? languageResources.selectAllTextInMaori
                                            : languageResources.selectAllText
                                        }
                                        <input type='checkbox' aria-label='HTML File' checked={fnAreAllSelected()} onChange={fnAreAllSelected() ? () => fnOnDeselectallClick() : () => fnOnSelectallClick()} />
                                    </p>
                                </div>
                                {Artifacts?.map((item: any, index: number): any => {
                                    if (!(item?.MediaName as string).includes('CogSearchIndexHelper')) {
                                        return (
                                            <li className='moe-doclist__item' key={index}>
                                                {renderDocument(item)}
                                            </li>
                                        );
                                    }
                                })}
                                <li className='moe-doclist__item'>
                                    {renderHTMLFile(data?.product?.result?.Name || productDetails?.product?.Name || '')}
                                </li>
                            </ul>
                        </>
                    )}
                </Node>
            </>
        );
    };

    async function fnDownloadClick(): Promise<void> {
        let selectedItems = Artifacts.filter(function(el: any) {
            return selectedArtifacts.indexOf(el.ArtifactId) >= 0;
        });

        // download_files(selectedItems);
        const urls = selectedItems
            .map((x: { MediaUrl: any; MediaName: any }) => {
                return { url: x.MediaUrl, mediaName: x.MediaName };
            })
            .filter((item: any) => !(item?.mediaName as string).includes('CogSearchIndexHelper'));
        await download(urls);

        if (resourcePageURL) {
            let downloadURL = '';
            if (resourcePageURL.indexOf('?') > 0) {
                downloadURL = `${resourcePageURL}&download=1`;
            } else {
                downloadURL = `${resourcePageURL}?download=1`;
            }
            await downloadHTML(downloadURL, `${data.product?.result?.Name || productDetails?.product?.Name || ''}`);
        }

        var quickviewDiv = document.querySelector('.ms-quickView__dialog');
        var closeButton = quickviewDiv?.querySelector('.msc-modal__close-button') as HTMLElement;
        closeButton.click();
    }

    function saveFile(mediaItem: any) {
        const url = mediaItem.url;
        const mediaName = mediaItem.mediaName;

        return new Promise(function(resolve, reject) {
            // Get file name from url.
            var xhr = new XMLHttpRequest();
            xhr.responseType = 'blob';
            xhr.onload = function() {
                resolve(xhr);
            };
            xhr.onerror = reject;
            xhr.open('GET', url);
            xhr.send();
        }).then(function(xhr: any) {
            // var filename = url.substring(url.lastIndexOf('/') + 1).split('?')[0];
            var a = document.createElement('a');
            a.href = window.URL.createObjectURL(xhr.response); // xhr.response is a blob
            a.download = mediaName; // Set the file name.
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            return xhr;
        });
    }

    function download(urls: any) {
        var cur = Promise.resolve();
        urls.forEach(function(url: string) {
            cur = cur.then(function() {
                return saveFile(url);
            });
        });
        return cur;
    }

    async function downloadHTML(url: string, filename: string) {
        await fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
            })
            .catch(console.error);
    }

    function renderDownload(): JSX.Element {
        return (
            <>
                <div className='ms-quickView__product-download'>
                    <div className='ms-quickView__download-container'>
                        <div className='download-button'>
                            <button
                                disabled={isAddtoKeteDisabled()}
                                className='moe-btn moe-btn--primary-blue'
                                aria-label='Download'
                                title={
                                    preferredLanguage === 'maori' ? languageResources.downloadTextInMaori : languageResources.downloadText
                                }
                                onClick={() => fnDownloadClick()}
                            >
                                {preferredKeteSiteLanguage === 'maori'
                                    ? languageResources.downloadTextInMaori
                                    : languageResources.downloadText}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    function isAddtoKeteDisabled() {
        if (fnHaveSelectedChanged()) {
            return false;
        }
        if (selectedArtifacts.length === 0 && !resourcePageURL) {
            return true;
        } else {
            return false;
        }
    }

    function fnHaveSelectedChanged():boolean {
        //compare previouslySelectedArtifacts and selectedArtifacts
        if (previouslySelectedArtifacts.length === 0 && selectedArtifacts.length === 0 && previouslySelectedResourcePageURL === resourcePageURL) {
            return false;
        }
        if (previouslySelectedArtifacts.length !== selectedArtifacts.length || previouslySelectedResourcePageURL !== resourcePageURL) {
            return true;
        } else {
            let tempValue = true;

            //test all elements
            previouslySelectedArtifacts.sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" })
            ).forEach((element, index) => {
                tempValue = tempValue && element === selectedArtifacts[index]
            })

            //test the end item, as it's not in the array
            tempValue = tempValue && previouslySelectedResourcePageURL === resourcePageURL

            // if true then all test show no change, so invert value to return
            return !tempValue;
        }
    }

    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer} className=''>
                {ModalHeaderContent}
                <Node className='msc-quickview__heading'>{props.title}</Node>
            </Node>
            <Node {...ModalBodyContainer}>{renderBodyContent(props)}</Node>
            <Node {...ModalFooterContainer}>
                {moduleType === 'downloadResources' ? (
                    renderDownload()
                ) : (
                    <Node {...cartContainerProps}>
                        {product && (
                            <AddToCartComponent
                                addToCartText={!isEdit ? languageResources.addToKeteButtonText : languageResources.updateKeteButtonText}
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                data={{ product: product }}
                                artifactsData={Artifacts.filter(function(el: any) {
                                    return selectedArtifacts.indexOf(el.ArtifactId) >= 0;
                                })}
                                resourcePageURL={resourcePageURL}
                                productUrl={productUrl}
                                disabled={!fnHaveSelectedChanged()}
                                isEdit={isEdit}
                                cartLineID={cartLineData?.LineId}
                            ></AddToCartComponent>
                        )}
                    </Node>
                )}
            </Node>
        </Module>
    );
};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param props - Configuration for the quick view component.
 * @returns - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewPropsExtend<{}>> = (
    props: IQuickviewViewProps & IQuickviewPropsExtend<{}>
) => {
    const { quickView, quickViewButton, isModalOpen, config, resources, context } = props;
    let toolTip;

    const btnClassName = config.moduleType === 'downloadResources' ? 'downloadresources' : 'addtocart';

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredKeteSiteLanguage: any = getUserPreferredLanguage(KWR, ADD_TO_KETE_SITE_LANGUAGE, userPrefLang, toggleLang, context);

    if (config.moduleType === 'downloadResources') {
        preferredKeteSiteLanguage === 'maori'
            ? (toolTip = resources.downloadResourcesTextInMaori)
            : (toolTip = resources.downloadResourcesText);
    } else {
        preferredKeteSiteLanguage === 'maori' ? (toolTip = resources.addToKeteTextInMaori) : (toolTip = resources.addToKeteText);
    }

    const quickViewUpdatedProps: IModuleProps = {
        ...quickView,
        title: toolTip || config.iconText
    };
    return (
        <Module {...quickViewUpdatedProps}>
            <div className={`moe-btn-iconlink moe-btn-iconlink--${btnClassName}`}>
                {quickViewButton}
                {(config.iconTextInMaori || config.iconText) && (
                    <span className='moe-btn-iconlink__txt'>
                        {preferredKeteSiteLanguage === 'maori' ? config.iconTextInMaori : config.iconText}
                    </span>
                )}
            </div>
            {isModalOpen && renderQuickViewPopup(props)}
        </Module>
    );
};

export default QuickViewFunctionalComponent;
